import { PageBackdrop } from '@/atoms/PageBackdrop';
import Meta from '@/components/Meta';
import { useEnhancedRouter } from '@/hooks/useEnhancedRouter';
import { MetaType, SettingsType } from '@/types';
import { enhanceNavigationWithPersistentParams } from '@/utils/persistentParams';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { ReactNode, useEffect } from 'react';
import { AdminHeader } from './AdminHeader';
import styles from './Layout.module.scss';

const Footer = dynamic(() => import('./Footer').then((mod) => mod.Footer));
const Header = dynamic(() => import('./Header').then((mod) => mod.Header));
const Sidebar = dynamic(() => import('./Sidebar').then((mod) => mod.Sidebar));

type LayoutProps = {
  data: SettingsType;
  /**
   * Pass an empty meta data if not required (constant EMPTY_META)
   */
  meta: MetaType;
  children: ReactNode;
};

export function Layout({
  data,
  meta = { title: 'Остров мечты' },
  children,
}: LayoutProps) {
  const { menuFooter, mode, socials, disclaimer, copyright, address, phone } =
    data;
  const { pathname, query } = useEnhancedRouter();
  const isAdminPage = pathname.includes('/moderator');
  const isFrameView = query.frame && query.frame === 'true';

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      enhanceNavigationWithPersistentParams();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [pathname, query]);

  return (
    <div className={styles.root}>
      <div
        className={cn(styles.adaptive, {
          [styles.admin]: isAdminPage,
          [styles.frame]: isFrameView,
        })}
      >
        {data && !isAdminPage && !isFrameView && (
          <Header
            menu={data.menu}
            links={data.links}
            translations={data.translations.common}
          />
        )}
        {isAdminPage && <AdminHeader />}
        {children}
        {data && <Sidebar menu={data.menu} />}
        <PageBackdrop />
      </div>
      {data && !isFrameView && (
        <Footer
          menu={menuFooter}
          timeTable={mode}
          socials={socials}
          disclaimer={disclaimer}
          copyright={copyright}
          address={address}
          phone={phone}
        />
      )}
      <Meta
        title={meta.title}
        description={meta.description}
        image={meta.picture}
        canonical={meta.canonical}
        robots={meta.robots}
        keywords={meta.keywords}
        h1={meta.h1}
        text={meta.text}
      />
    </div>
  );
}
